import { getScrollParent } from '@/components/tiptap-editor/tiptap-extension/math-extension/utils';
import type { CollaborationActiveNodeState, CollaborationPresenceUser } from '@/lib/collaboration-presence';
import { scrollPaneToDocPosAtViewportTop } from '@/lib/split-view-scroll-sync';
import type { TiptapCollabProvider } from '@tiptap-pro/provider';
import type { Editor } from '@tiptap/react';
import { relativePositionToAbsolutePosition, ySyncPluginKey } from '@tiptap/y-tiptap';
import * as Y from 'yjs';

type AwarenessCursor = { anchor: unknown; head: unknown };

function findNodePosByActiveNode(editor: Editor, activeNode: CollaborationActiveNodeState): number | null {
    let found: number | null = null;
    editor.state.doc.descendants((node, pos) => {
        if (found !== null) {
            return false;
        }
        const id = (node.attrs as { id?: string }).id;
        if (id === activeNode.nodeId && node.type.name === activeNode.nodeType) {
            found = pos;
            return false;
        }
    });
    return found;
}

function resolveCursorDocPos(editor: Editor, cursorHead: unknown): number | null {
    const ystate = ySyncPluginKey.getState(editor.state);
    if (!ystate?.doc || !ystate.type || !ystate.binding) {
        return null;
    }

    const absolutePos = relativePositionToAbsolutePosition(
        ystate.doc,
        ystate.type,
        Y.createRelativePositionFromJSON(cursorHead),
        ystate.binding.mapping,
    );
    if (absolutePos === null) {
        return null;
    }

    const maxSize = Math.max(editor.state.doc.content.size - 1, 0);
    return Math.min(absolutePos, maxSize);
}

function scrollEditorToDocPos(editor: Editor, pos: number): void {
    if (editor.view.dom.closest('[data-split-pane-scroll="true"]')) {
        scrollPaneToDocPosAtViewportTop(editor, pos);
        return;
    }

    const coords = editor.view.coordsAtPos(pos);
    const scrollParent = getScrollParent(editor.view.dom);
    if (scrollParent instanceof HTMLElement) {
        const scrollRect = scrollParent.getBoundingClientRect();
        const targetScrollTop = scrollParent.scrollTop + (coords.top - scrollRect.top) - scrollRect.height / 2;
        scrollParent.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
        return;
    }

    window.scrollTo({
        top: window.scrollY + coords.top - window.innerHeight / 2,
        behavior: 'smooth',
    });
}

export function getCollaborationUserDocPos(editor: Editor, provider: TiptapCollabProvider, user: CollaborationPresenceUser): number | null {
    const awarenessState = provider.awareness?.getStates().get(user.clientId);
    const cursor = awarenessState?.cursor as AwarenessCursor | undefined;
    if (cursor?.head) {
        const cursorPos = resolveCursorDocPos(editor, cursor.head);
        if (cursorPos !== null) {
            return cursorPos;
        }
    }

    const activeNode = awarenessState?.activeNode as CollaborationActiveNodeState | undefined;
    if (activeNode?.nodeId && activeNode?.nodeType) {
        return findNodePosByActiveNode(editor, activeNode);
    }

    return null;
}

export function scrollToCollaborationUser(editor: Editor, provider: TiptapCollabProvider, user: CollaborationPresenceUser): boolean {
    const pos = getCollaborationUserDocPos(editor, provider, user);
    if (pos === null) {
        return false;
    }

    scrollEditorToDocPos(editor, pos);
    return true;
}
